<template>
    <TypeAhead
        class="flex-fill"
        placeholder="general.search"
        :ignoreMinvalueOnInteger="true"
        v-model:model-value="value"
        :datasources="[datasource]"
        :autoresize="true"
        ref="typeahead"
        @item-selected="emit('item-selected', $event)"
        @focus="isSearchFocused = true"
        @blur="isSearchFocused = false"
    ></TypeAhead>
</template>
<script setup lang="ts">
import { TypeAhead } from '@/components';
import { ref } from 'vue';
import { SearchRequest, TypeAheadItem, TypeAheadResponse } from '@/objects/generated';
import { TypeAheadDataSource, type TypeAheadExpose } from '@/components/Typeahead';
import { useContestantStore } from '@/stores';

defineOptions({ inheritAttrs: false });

// If the search filed is focused or not
const isSearchFocused = ref(false);

// The value of the search
const value = ref<string>('');

// The typeahead component
const typeahead = ref<TypeAheadExpose | null>(null);

const contestantStore = useContestantStore();

/**
 * The datasource for the typeahead
 */
const datasource = new TypeAheadDataSource('Contains', async (request) => {
    const response = TypeAheadResponse.FromJsValues({
        Items: await doSearch(request),
    });
    return response;
});

defineExpose<TypeAheadExpose>({ focus: () => typeahead.value?.focus() });

/**
 * Do the actual search for contestant based on the search query. If the query is numeric, we search for bib, otherwise we search for firstname and surname contains
 * @param search
 */
const doSearch = async (search: SearchRequest): Promise<TypeAheadItem[]> => {
    await contestantStore.EnsureLoaded(['Contestants']);

    // We need to check if the query is a numeric, then we search for bib
    const isNumeric = !isNaN(Number(search.Query));

    if (isNumeric) {
        const bib = Number(search.Query);
        const result = contestantStore.Contestants.filter((c) => c.Bib === bib);

        return result.map((c) =>
            TypeAheadItem.FromJsValues({
                ID: c.ID,
                DataType: 'Contestant',
                Text: contestantStore.GetDisplayNameForContestantObject(c, 'fullwithbib'),
                Description: '',
            }),
        );
    } else {
        // We search firstname and surname with contains
        const query = search.Query?.toLowerCase() ?? '';
        const result = contestantStore.Contestants.filter((c) => {
            const fullName = `${c.Firstname} ${c.Surname}`.toLowerCase();
            return fullName.includes(query);
        });

        return result.map((c) =>
            TypeAheadItem.FromJsValues({
                ID: c.ID,
                DataType: 'Contestant',
                Text: contestantStore.GetDisplayNameForContestantObject(c, 'fullwithbib'),
                Description: '',
            }),
        );
    }
};

const emit = defineEmits<{
    (e: 'item-selected', item: TypeAheadItem): void;
}>();
</script>
